import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DomainSelector from '../components/shared/DomainSelector';
import { useData } from '../contexts/DataContext';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { useNotification } from '../contexts/NotificationContext';
import { CustomerGroupSelector } from '../components/shared/CustomerGroupSelector';
import { ArticleUrlInput } from '../components/SocialMediaPosts/ArticleUrlInput';
import { NewsletterDrafts } from '../components/Newsletter/NewsletterDrafts';
import { StreamingModal, StreamingProgressItem } from '../components/shared/StreamingModal';
import { aiService } from '../services/aiService';
import { Domain } from '../types/data';
import { extractErrorMessageFromResponse } from '../services/errorService';
import * as Sentry from '@sentry/react';
import RichTextEditor from '../components/shared/RichTextEditor';
import { SourceTextForGenerating } from '../components/SocialMediaPosts/SourceTextForGenerating';
import InputMethodSelector, { InputMethod } from '../components/shared/InputMethodSelector';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';
import { FeatureFlag } from '../utils/featureFlags';

interface NewsletterDraft {
  title: string;
  content: string;
  date: string;
  charCount: number;
}

const Newsletter: React.FC = () => {
  const { domains, targetCompany, customerTargetGroups, fetchRelatedDomainData, fetchDomains } =
    useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const [inputMethod, setInputMethod] = useState<InputMethod>('url');
  const [articleUrls, setArticleUrls] = useState<string[]>(['']);
  const [sourceText, setSourceText] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingDrafts, setStreamingDrafts] = useState<Record<number, string>>({
    0: '',
    1: '',
    2: '',
  });
  const [streamingStatus, setStreamingStatus] = useState<Record<number, string>>({
    0: 'waiting',
    1: 'waiting',
    2: 'waiting',
  });
  const [generatedDrafts, setGeneratedDrafts] = useState<NewsletterDraft[]>([]);
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const multipleUrls = true;

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    loadData();
  }, [domains, targetCompany]);

  const loadData = () => {
    if (targetCompany && domains.length > 0) {
      const foundDomain = domains.find((domain: Domain) => domain.company_id === targetCompany);
      if (foundDomain && typeof foundDomain !== 'undefined') {
        setActiveDomain(foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  };

  const generateSingleDraft = async (index: number) => {
    try {
      const urls = articleUrls.filter((url) => url.trim() !== '');
      const generationContent =
        inputMethod === 'url'
          ? `Article URLs: ${urls.join(', ')}`
          : `Article content: ${sourceText}`;

      const { data: response, error: generateNewsletterError } = await aiService.generateNewsletter(
        {
          hasUrl: inputMethod === 'url',
          articleUrls: urls || [''],
          prompt: generationContent,
          draftIndex: index + 1,
          language: activeDomain?.language || 'English',
          toneOfVoice: activeDomain?.tone_of_voice || 'Professional',
          customerGroupDescription:
            customerTargetGroups.find((group) => group.name === selectedCustomerGroup)
              ?.description || '',
          companyId: targetCompany || '',
          domainName: activeDomain?.domain || '',
          customPrompt: customPrompt,
        }
      );

      if (generateNewsletterError) {
        throw new Error(await extractErrorMessageFromResponse(generateNewsletterError, t));
      }

      if (!response.body) {
        showNotification('Error generating newsletter', 'error');
        throw new Error('No response body received');
      }

      const decoder = new TextDecoder();
      let content = '';

      const reader = response.body.getReader();
      setStreamingStatus((prev) => ({ ...prev, [index]: 'generating' }));

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) {
                  content += data.content;
                  setStreamingDrafts((prev) => ({
                    ...prev,
                    [index]: content,
                  }));
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      setStreamingStatus((prev) => ({ ...prev, [index]: 'completed' }));
      return content;
    } catch (error) {
      Sentry.captureException(error);
      showNotification(
        error instanceof Error
          ? error.message
          : 'Error generating newsletter draft. Please try again.',
        'error'
      );
      console.error(`Error generating draft ${index}:`, error);
      setStreamingStatus((prev) => ({ ...prev, [index]: 'error' }));
      throw error;
    }
  };

  const handleSubmit = async () => {
    const isUrlValid = inputMethod === 'url' && articleUrls.some((url) => url.trim() !== '');
    const isTextValid = inputMethod === 'text' && sourceText.trim();

    if (!selectedCustomerGroup || (!isUrlValid && !isTextValid)) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setIsGenerating(true);
    setGeneratedDrafts([]);
    setStreamingDrafts({ 0: '', 1: '', 2: '' });
    setStreamingStatus({ 0: 'waiting', 1: 'waiting', 2: 'waiting' });

    try {
      const drafts = await Promise.all([
        generateSingleDraft(0),
        generateSingleDraft(1),
        generateSingleDraft(2),
      ]);

      const formattedDrafts: NewsletterDraft[] = drafts.map((content, index) => ({
        title: extractSubject(content) || `Newsletter Draft ${index + 1}`,
        content,
        date: new Date().toLocaleString(),
        charCount: content.length,
      }));

      setGeneratedDrafts(formattedDrafts);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error generating newsletters:', error);
      showNotification(
        error instanceof Error ? error.message : 'Error generating content. Please try again.',
        'error'
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const extractSubject = (content: string): string | null => {
    const subjectMatch = content.match(/Subject:(.+?)(\n|$)/i);
    return subjectMatch ? subjectMatch[1].trim() : null;
  };

  if (!targetCompany || !activeDomain) {
    return (
      <StatusMessage targetCompany={targetCompany} activeDomain={activeDomain?.domain || null} />
    );
  }

  return (
    <FeatureFlagOverlay feature={FeatureFlag.NEWSLETTER}>
      <div className="p-8">
        <ErrorBoundary>
          <h1 className="text-3xl font-bold mb-2">{t('newsletter.title')}</h1>
          <div className="mb-6 mt-4">
            <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
          </div>

          <div className="mb-6">
            <CustomerGroupSelector
              customerGroups={
                customerTargetGroups?.map((group) => ({
                  name: group.name,
                  description: group.description || '',
                })) || []
              }
              selectedGroup={selectedCustomerGroup}
              onSelect={setSelectedCustomerGroup}
            />
          </div>

          <div className="mb-6">
            <InputMethodSelector
              inputMethod={inputMethod}
              onInputMethodChange={setInputMethod}
              translationNamespace="newsletter"
            />
          </div>

          {inputMethod === 'url' && (
            <div className="mb-6">
              <ArticleUrlInput
                value={articleUrls}
                onChange={(urls) => setArticleUrls(Array.isArray(urls) ? urls : [urls])}
                multipleUrls={multipleUrls}
              />
            </div>
          )}

          {inputMethod === 'text' && (
            <div className="mb-6">
              <SourceTextForGenerating value={sourceText} onChange={setSourceText} />
            </div>
          )}

          <ErrorBoundary>
            <RichTextEditor
              label={t('common.custom_prompt')}
              value={customPrompt}
              onChange={setCustomPrompt}
              simpleText={true}
            />
          </ErrorBoundary>

          <button
            onClick={handleSubmit}
            disabled={isGenerating || !selectedCustomerGroup}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:bg-gray-400"
          >
            {isGenerating ? t('newsletter.generating') : t('newsletter.generate_drafts')}
          </button>
          <ErrorBoundary>
            {isGenerating && (
              <StreamingModal
                title={t('common.generating_newsletter')}
                items={Object.keys(streamingDrafts).map(
                  (index): StreamingProgressItem => ({
                    id: parseInt(index),
                    title: `${t('newsletter.title')} ${parseInt(index) + 1}`,
                    status: streamingStatus[parseInt(index)] || 'waiting',
                    content: streamingDrafts[parseInt(index)],
                    showCharCount: true,
                  })
                )}
                variant="default"
              />
            )}
          </ErrorBoundary>
          <ErrorBoundary>
            {generatedDrafts.length > 0 && <NewsletterDrafts drafts={generatedDrafts} />}
          </ErrorBoundary>
        </ErrorBoundary>
      </div>
    </FeatureFlagOverlay>
  );
};

export default Newsletter;
